import { useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { NavIcon } from './NavIcon.js';
import { StrikerBrand, type BrandSurface } from './StrikerBrand.js';

export interface ShellNavItem {
  key: string;
  label: string;
  path: string;
  /** Icon name for NavIcon. Unused when text-only SaaS parity is on. */
  icon?: string;
  desktopOnly?: boolean;
  children?: ShellNavItem[];
  /** When true, the item is shown but dimmed with a lock icon and links to the store. */
  locked?: boolean;
  /** Optional badge count to display next to the label (e.g. open SOS count). */
  badgeCount?: number;
  /** Badge tone: error (red), warn (yellow), info (blue). */
  badgeTone?: 'error' | 'warn' | 'info';
}

export interface AppShellProps {
  brand?: string;
  /** @deprecated Prefer brandSurface + StrikerBrand; kept for callers that pass a custom img. */
  logoSrc?: string;
  /** Dark topbar → light wordmark; light chrome → dark wordmark. */
  brandSurface?: BrandSurface;
  homePath?: string;
  /** Shown in topbar as “Console / {label}” when not on /master. */
  consoleLabel?: string;
  navItems: ShellNavItem[];
  topbarRight?: ReactNode;
  children: ReactNode;
}

function isPathActive(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function AppShell({
  brand = 'StrikerPos',
  logoSrc,
  brandSurface = 'dark',
  homePath = '/tenant',
  consoleLabel = 'Tenant',
  navItems,
  topbarRight,
  children,
}: AppShellProps) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({
    menu: true,
    tables: true,
    settings: true,
    tenants: true,
    'system-config': true,
  });

  const isMaster = location.pathname.startsWith('/master');
  const consoleTitle = isMaster ? 'Console Master' : `Console / ${consoleLabel}`;

  const defaultExpanded = useMemo(() => {
    const next: Record<string, boolean> = { ...expandedNodes };
    for (const item of navItems) {
      if (item.children?.length && isPathActive(location.pathname, item.path)) {
        next[item.key] = true;
      }
    }
    return next;
  }, [navItems, location.pathname, expandedNodes]);

  function closeMobile() {
    setMobileOpen(false);
  }

  function toggleExpanded(key: string) {
    setExpandedNodes((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  function renderNavItem(item: ShellNavItem) {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const expanded = Boolean(defaultExpanded[item.key]);
    const parentActive = isPathActive(location.pathname, item.path);
    const visibility = item.desktopOnly ? 'sk-shell__nav-node--desktop' : '';
    const lockedClass = item.locked ? ' sk-shell__link--locked' : '';

    return (
      <div className={`sk-shell__nav-node ${visibility}`.trim()} key={item.key}>
        <div className="sk-shell__nav-row">
          <NavLink
            to={item.locked ? '/tenant/store' : item.path}
            className={({ isActive }) =>
              `sk-shell__link${isActive || parentActive ? ' sk-shell__link--active' : ''}${lockedClass}`
            }
            onClick={closeMobile}
            title={item.locked ? `${item.label} — Modulo non attivo. Vai allo Store per attivarlo.` : undefined}
          >
            {(
              <span className="sk-shell__link-icon">
                <NavIcon name={item.icon || 'list'} className="sk-shell__icon" />
              </span>
            )}
            <span className="sk-shell__label">{item.label}</span>
            {item.badgeCount != null && item.badgeCount > 0 ? (
              <span className={`sk-mc-count sk-mc-count--${item.badgeTone || 'error'}`}>
                {item.badgeCount > 99 ? '99+' : item.badgeCount}
              </span>
            ) : null}
            {item.locked && <span className="sk-shell__lock" aria-label="Modulo bloccato">🔒</span>}
          </NavLink>
          {hasChildren ? (
            <button
              type="button"
              className="sk-shell__expand"
              aria-expanded={expanded}
              aria-label={expanded ? `Comprimi ${item.label}` : `Espandi ${item.label}`}
              onClick={() => toggleExpanded(item.key)}
            >
              {expanded ? '▾' : '▸'}
            </button>
          ) : null}
        </div>
        {hasChildren && expanded ? (
          <div className="sk-shell__nav-children">
            {item.children!.map((child) => (
              <NavLink
                key={child.key}
                to={child.locked ? '/tenant/store' : child.path}
                className={({ isActive }) =>
                  `sk-shell__link sk-shell__link--child${isActive ? ' sk-shell__link--active' : ''}${
                    child.desktopOnly ? ' sk-shell__link--desktop' : ''
                  }${child.locked ? ' sk-shell__link--locked' : ''}`
                }
                onClick={closeMobile}
                title={child.locked ? `${child.label} — Modulo non attivo. Vai allo Store per attivarlo.` : undefined}
              >
                {(
                  <span className="sk-shell__link-icon">
                    <NavIcon name={child.icon || 'list'} className="sk-shell__icon" />
                  </span>
                )}
                <span className="sk-shell__label">{child.label}</span>
                {child.badgeCount != null && child.badgeCount > 0 ? (
                  <span className={`sk-mc-count sk-mc-count--${child.badgeTone || 'error'}`}>
                    {child.badgeCount > 99 ? '99+' : child.badgeCount}
                  </span>
                ) : null}
                {child.locked && <span className="sk-shell__lock" aria-label="Modulo bloccato">🔒</span>}
              </NavLink>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`sk-shell${mobileOpen ? ' sk-shell--mobile-open' : ''}`}>
      <div className="sk-shell__atmosphere" aria-hidden="true" />

      <button
        type="button"
        className="sk-shell__backdrop"
        aria-label="Chiudi menu"
        onClick={closeMobile}
      />

      <header className="sk-shell__topbar">
        <div className="sk-shell__topbar-inner">
          <div className="sk-shell__topbar-left">
            <button
              type="button"
              className="sk-shell__menu-btn"
              aria-label="Apri menu"
              onClick={() => setMobileOpen(true)}
            >
              <NavIcon name="menu" className="sk-shell__icon" />
            </button>
            <Link to={homePath} className="sk-shell__logo-frame" aria-label={brand}>
              {logoSrc ? (
                <img className="sk-shell__logo" src={logoSrc} alt={brand} />
              ) : (
                <StrikerBrand surface={brandSurface} title={brand} className="sk-shell__brand" />
              )}
            </Link>
            <div className="sk-shell__console">
              <h1 className="sk-shell__console-title">{consoleTitle}</h1>
              <p className="sk-shell__console-path">{location.pathname}</p>
            </div>
          </div>
          <div className="sk-shell__topbar-right">{topbarRight}</div>
        </div>
      </header>

      <div className="sk-shell__body">
        <aside className="sk-shell__sidebar" aria-label="Navigazione principale">
          <nav className="sk-shell__nav">{navItems.map(renderNavItem)}</nav>
        </aside>
        <main className="sk-shell__content">{children}</main>
      </div>
    </div>
  );
}
